import { PASSWORD_MIN_LENGTH, PERMISSIONS, ROLE_LABELS, type NotificationDto, type Page, type TaskSummaryDto } from '@edms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { ADMIN } from '../layout';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';
import { useInboxCounts } from '../lib/tasks-ui';
import { Avatar, Empty, formatDate, Pager, useToast } from '../lib/ui';
import { NotificationSettingsCard, PushBanner } from '../components/notification-settings';
import { NewsWidget } from './news';
import { SubstitutionsCard } from './substitutions';
import { TaskRow } from './tasks';

function greeting(): string {
  const h = new Date().getHours();
  if (h < 5) return 'Доброй ночи';
  if (h < 12) return 'Доброе утро';
  if (h < 18) return 'Добрый день';
  return 'Добрый вечер';
}

export function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const firstName = user!.fullName.split(' ')[1] ?? user!.fullName;
  const counts = useInboxCounts();
  const inbox = useQuery({
    queryKey: ['tasks', 'list', { scope: 'inbox', home: true }],
    queryFn: () => api.get<Page<TaskSummaryDto>>('/tasks', { scope: 'inbox', limit: 5 }),
  });
  const c = counts.data;
  const waiting = c ? c.approvals + c.assignments + c.reviews : null;

  return (
    <>
      <section className="hero">
        <h1>
          {greeting()}, {firstName}
        </h1>
        <p>
          {waiting === null
            ? 'Загружаем, что ждёт вашего решения…'
            : waiting === 0
              ? 'Ничего не ждёт вашего решения. Можно создать документ на согласование или поручение.'
              : `Ждут вашего решения: ${waiting}. Начните с тех, у которых ближе срок.`}
        </p>
        <div className="hero-actions">
          <Link to="/tasks/new" className="btn hero-btn">
            <Icon name="plus" size={18} /> Документ или заявка
          </Link>
          <Link to="/tasks/new?kind=assignment" className="btn hero-btn ghost">
            <Icon name="plus" size={18} /> Поручение
          </Link>
        </div>
      </section>
      <PushBanner />
      <div className="grid-cards">
        <Link to="/inbox" className="card stat">
          <span className="label">Согласовать</span>
          <span className="value">{c?.approvals ?? '…'}</span>
          <span className="faint">документы ждут вашего решения</span>
        </Link>
        <Link to="/inbox" className="card stat">
          <span className="label">Выполнить</span>
          <span className="value">{c?.assignments ?? '…'}</span>
          <span className="faint">поручения и подзадачи на вас</span>
        </Link>
        <Link to="/inbox" className="card stat">
          <span className="label">Проверить</span>
          <span className="value">{c?.reviews ?? '…'}</span>
          <span className="faint">исполнители прислали результат</span>
        </Link>
        <Link to="/tasks?scope=mine" className="card stat">
          <span className="label">Мои документы</span>
          <span className="value" style={{ fontSize: '1.2rem' }}>
            Открыть →
          </span>
          <span className="faint">{ROLE_LABELS[user!.role]}{user!.departmentName ? ` · ${user!.departmentName}` : ''}</span>
        </Link>
      </div>
      {inbox.data && inbox.data.items.length > 0 && (
        <section className="card" style={{ marginTop: 20 }}>
          <div className="section-head card-pad" style={{ paddingBottom: 0 }}>
            <h2>Ждут меня</h2>
            <Link to="/inbox">все →</Link>
          </div>
          <div className="table-wrap">
            <table className="table">
              <tbody>
                {inbox.data.items.map((t) => (
                  <TaskRow key={t.id} t={t} onOpen={() => navigate(`/tasks/${t.id}`)} />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      <NewsWidget />
    </>
  );
}

export function ProfilePage() {
  const { user, can } = useAuth();
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const change = useMutation({
    mutationFn: () => api.post('/auth/change-password', { currentPassword: current, newPassword: next }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      toast('Пароль изменён. На других устройствах нужно войти заново.');
    },
  });
  const err = change.error instanceof ApiError ? change.error : null;
  const adminLinks = ADMIN.filter((i) => !i.soon && (!i.permission || can(i.permission)));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    change.mutate();
  };

  return (
    <>
      <div className="page-head">
        <div className="person">
          <Avatar name={user!.fullName} size={64} />
          <div>
            <h1>{user!.fullName}</h1>
            <p className="muted">
              {[user!.position, user!.departmentName, ROLE_LABELS[user!.role]].filter(Boolean).join(' · ')}
            </p>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', alignItems: 'start' }}>
        <form className="card card-pad" style={{ display: 'grid', gap: 14 }} onSubmit={submit}>
          <h2>Сменить пароль</h2>
          {err && !Object.keys(err.fields).length && <div className="alert">{err.message}</div>}
          <label className="field">
            <span>Текущий пароль</span>
            <input className="input" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} aria-invalid={Boolean(err?.fields.currentPassword)} />
            {err?.fields.currentPassword && <span className="field-error">{err.fields.currentPassword}</span>}
          </label>
          <label className="field">
            <span>Новый пароль</span>
            <input className="input" type="password" autoComplete="new-password" minLength={PASSWORD_MIN_LENGTH} value={next} onChange={(e) => setNext(e.target.value)} aria-invalid={Boolean(err?.fields.newPassword)} />
            <span className="faint">Не короче {PASSWORD_MIN_LENGTH} символов</span>
            {err?.fields.newPassword && <span className="field-error">{err.fields.newPassword}</span>}
          </label>
          <button className="btn btn-primary" disabled={change.isPending || !current || !next}>
            Сохранить
          </button>
        </form>

        <div className="card card-pad" style={{ display: 'grid', gap: 12 }}>
          <h2>Мои права</h2>
          <p className="faint">{user!.email}</p>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>
            {user!.permissions.map((p) => (
              <li key={p}>{PERMISSIONS[p]}</li>
            ))}
          </ul>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <NotificationSettingsCard />
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <SubstitutionsCard />
        </div>

        {adminLinks.length > 0 && (
          <div className="card card-pad" style={{ display: 'grid', gap: 6 }}>
            <h2 style={{ marginBottom: 6 }}>Управление</h2>
            {adminLinks.map((i) => (
              <Link key={i.to} to={i.to} className="list-link">
                <Icon name={i.icon} />
                {i.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

const LIMIT = 30;

export function NotificationsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [offset, setOffset] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const list = useQuery({
    queryKey: ['notifications', 'list', offset, unreadOnly],
    queryFn: () => api.get<Page<NotificationDto>>('/notifications', { limit: LIMIT, offset, unread: unreadOnly }),
  });
  const markRead = useMutation({
    mutationFn: (ids?: string[]) => api.post('/notifications/read', ids ? { ids } : {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const open = (n: NotificationDto) => {
    if (!n.readAt) markRead.mutate([n.id]);
    if (n.link) navigate(n.link);
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Уведомления</h1>
          <p className="muted">Всё, что касается ваших задач и согласований</p>
        </div>
        <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link to="/profile#notifications" className="btn btn-ghost">
            Настроить
          </Link>
          <button className="btn" onClick={() => markRead.mutate(undefined)} disabled={markRead.isPending}>
            Прочитать все
          </button>
        </span>
      </div>
      <div className="card">
        <div className="toolbar">
          <label className="check">
            <input type="checkbox" checked={unreadOnly} onChange={(e) => { setUnreadOnly(e.target.checked); setOffset(0); }} />
            Только новые
          </label>
        </div>
        {list.data && list.data.items.length === 0 && <Empty emoji="🌿" title="Пока тихо">Новых событий нет</Empty>}
        <div role="list">
          {list.data?.items.map((n) => (
            <button
              key={n.id}
              role="listitem"
              onClick={() => open(n)}
              className="btn-ghost"
              style={{
                display: 'grid',
                gap: 4,
                width: '100%',
                padding: '14px 18px',
                border: 0,
                borderBottom: '1px solid var(--border)',
                background: n.readAt ? 'transparent' : 'var(--brand-tint)',
                color: 'inherit',
                font: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {!n.readAt && <span className="dot" style={{ color: 'var(--brand)' }} aria-label="новое" />}
                <b>{n.title}</b>
                <span className="faint" style={{ marginLeft: 'auto' }}>
                  {formatDate(n.createdAt)}
                </span>
              </span>
              {n.body && <span className="muted" style={{ whiteSpace: 'pre-line' }}>{n.body}</span>}
            </button>
          ))}
        </div>
        {list.data && <Pager total={list.data.total} limit={LIMIT} offset={offset} onChange={setOffset} />}
      </div>
    </>
  );
}
